import { toSafeAppPath } from '@/lib/utils/safe-app-path'

const AUTH_REDIRECT_BLOCKLIST = [
  '/login',
  '/login/confirmation',
  '/register',
  '/register/confirmation',
  '/register/resend-code',
  '/auth/callback',
  '/auth/google/callback',
  '/auth/google-calendar-callback',
  '/auth/gmail-callback',
  '/auth/outlook-callback',
  '/auth/outlook-calendar-callback',
  '/invite',
  '/unauthorized',
] as const

const DEFAULT_AUTH_REDIRECT = '/organizations'

const decodeRedirectParam = (value: string | null): string | null => {
  if (!value) return null
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const normalizePathname = (value: string): string =>
  value.split(/[?#]/)[0] || ''

const isBlockedRedirect = (pathname: string): boolean =>
  AUTH_REDIRECT_BLOCKLIST.some(blocked => pathname.startsWith(blocked))

export const getAuthRedirectTarget = (
  search: string,
  currentPath: string,
  fallback: string = DEFAULT_AUTH_REDIRECT,
): string => {
  const params = new URLSearchParams(search)
  const rawRedirect = params.get('redirect') || params.get('returnUrl')
  const decoded = decodeRedirectParam(rawRedirect)

  // Only a plain single-slash app path: `//host`, backslashes, absolute URLs
  // and traversal would leave the app after sign-in.
  const safe = toSafeAppPath(decoded)
  if (!safe) {
    return fallback
  }

  const targetPathname = normalizePathname(safe)
  if (!targetPathname || targetPathname === currentPath) {
    return fallback
  }

  if (isBlockedRedirect(targetPathname)) {
    return fallback
  }

  return safe
}

/**
 * A `redirect` / `returnUrl` style parameter as a safe in-app path, or null.
 * Decodes once (the parameter is often encoded twice) and then requires a
 * plain same-origin, single-slash app path, so `//evil.example`,
 * `/\\evil.example`, `%2F%2Fevil.example` and absolute URLs are refused.
 */
export const safeRedirectTarget = (
  value: string | null | undefined,
): string | null => toSafeAppPath(decodeRedirectParam(value ?? null))
